import React, { useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryStates, parseAsInteger, parseAsString } from 'nuqs';
import { ColumnDef } from '@tanstack/react-table';
import { formatDistanceToNow } from 'date-fns';
import { Time } from '@/components/ui/time';
import {
  Cloud,
  Shield,
  Route,
  Server,
  Network,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import {
  DataTable,
  DataTableContainer,
  DataTableContent,
  DataTableHeader,
  DataTableRows,
  DataTableWrapper,
  DataTableFooter,
  DataTableUtilityBar,
} from '@/components/data-table';
import { useEdgeLinkBridges } from '@/lib/api/edgelink';
import { EdgeLinkBridge } from '@/lib/api/edgelink/types';
import { ConnectionType } from '@/lib/api/types.gen';

function ConnectionTypeBadge({ type }: { type: ConnectionType }) {
  const config = {
    [ConnectionType.Cloudflared]: {
      label: 'Cloudflare Tunnel',
      icon: Cloud,
      className: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
    },
    [ConnectionType.CloudflareIpsec]: {
      label: 'IPSec VPN',
      icon: Shield,
      className: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    },
  }[type] || {
    label: type,
    icon: Network,
    className: 'bg-gray-500/10 text-gray-400 border-gray-500/30',
  };

  const Icon = config.icon;

  return (
    <Badge variant="outline" className={`${config.className} font-semibold`}>
      <Icon className="h-3 w-3 mr-1.5" />
      {config.label}
    </Badge>
  );
}

const bridgeColumns: ColumnDef<EdgeLinkBridge>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    enableHiding: false,
    enableSorting: true,
    meta: { label: 'Bridge Name' },
    header: 'Bridge Name',
    cell: ({ row }) => {
      const { name, id, type } = row.original;

      const getIcon = () => {
        switch (type) {
          case ConnectionType.Cloudflared:
            return <Cloud className="h-5 w-5 text-lime" />;
          case ConnectionType.CloudflareIpsec:
            return <Shield className="h-5 w-5 text-lime" />;
          default:
            return <Server className="h-5 w-5 text-lime" />;
        }
      };

      return (
        <Link
          to={`/edgelink/${id}`}
          className="flex items-center gap-3 font-semibold text-lime hover:underline"
        >
          {getIcon()}
          {name}
        </Link>
      );
    },
  },
  {
    id: 'type',
    accessorKey: 'type',
    enableSorting: false,
    meta: { label: 'Connection Type' },
    header: 'Connection Type',
    cell: ({ row }) => <ConnectionTypeBadge type={row.original.type} />,
  },
  {
    id: 'routes',
    enableSorting: false,
    meta: { label: 'Routes' },
    header: 'Routes',
    cell: ({ row }) => {
      const { routes } = row.original;

      return (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2">
                <Route className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">
                  {routes.length} route{routes.length !== 1 ? 's' : ''}
                </span>
              </div>
            </TooltipTrigger>
            {routes.length > 0 && (
              <TooltipContent side="right" className="max-w-xs">
                <div className="space-y-1">
                  {routes.map((route) => (
                    <div key={route.id} className="font-mono text-xs">
                      {route.edgeAddressRangeCidr}
                    </div>
                  ))}
                </div>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      );
    },
  },
  {
    id: 'version',
    enableSorting: false,
    meta: { label: 'Version' },
    header: 'Version',
    cell: ({ row }) => {
      const { currentClientVersion, lastSeenAt } = row.original;

      if (!currentClientVersion) {
        return <span className="text-muted-foreground text-sm">Not reported</span>;
      }

      return (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Badge variant="outline" className="bg-lime/10 text-lime border-lime/30 font-mono">
                  v{currentClientVersion}
                </Badge>
              </div>
            </TooltipTrigger>
            {lastSeenAt && (
              <TooltipContent side="right">
                Last seen {formatDistanceToNow(new Date(lastSeenAt), { addSuffix: true })}
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      );
    },
  },
  {
    id: 'createdAt',
    accessorKey: 'createdAt',
    enableSorting: true,
    meta: { label: 'Created' },
    header: 'Created',
    cell: ({ row }) => (
      <span className="text-muted-foreground">
        <Time date={row.original.createdAt} format="date" />
      </span>
    ),
  },
];

// URL param parsers
const edgelinkUrlParsers = {
  page: parseAsInteger.withDefault(1),
  pageSize: parseAsInteger.withDefault(20),
  search: parseAsString,
};

export function EdgeLinkTable() {
  const navigate = useNavigate();
  const [urlParams, setUrlParams] = useQueryStates(edgelinkUrlParsers, { history: 'replace' });

  const page = urlParams.page;
  const pageSize = urlParams.pageSize;
  const searchName = urlParams.search ?? '';

  const { data, isLoading, error } = useEdgeLinkBridges(page, pageSize);

  // Client-side filter by name (API doesn't support search)
  const filteredData = useMemo(() => {
    if (!data?.items) return [];
    if (!searchName) return data.items;
    const lower = searchName.toLowerCase();
    return data.items.filter((b) => b.name.toLowerCase().includes(lower));
  }, [data?.items, searchName]);

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setUrlParams({ pageSize: newPageSize, page: 1 });
    },
    [setUrlParams]
  );

  return (
    <div className="relative">

      <DataTable
        columns={bridgeColumns}
        data={filteredData}
        loading={isLoading && !data}
        error={error}
        page={page}
        pageSize={pageSize}
        totalCount={data?.totalCount}
        onPageChange={(p: number) => setUrlParams({ page: p })}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={[5, 10, 20, 50]}
        stateKey="edgelink-table"
        emptyMessage="No EdgeLink bridges found"
      >
        <DataTableContainer>
          <DataTableContent>
            <DataTableUtilityBar
              showColumnVisibility
              search={{
                value: searchName,
                onChange: (value) => setUrlParams({ search: value || null, page: 1 }),
                placeholder: 'Search bridge name...',
              }}
              actions={
                <Button
                  size="sm"
                  onClick={() => navigate('/edgelink/add')}
                  data-onboarding="edgelink-add-button"
                >
                  Add Bridge
                </Button>
              }
            />
            <DataTableWrapper>
              <DataTableHeader />
              <DataTableRows />
            </DataTableWrapper>
            <DataTableFooter />
          </DataTableContent>
        </DataTableContainer>
      </DataTable>
    </div>
  );
}

export default function EdgeLinkPage() {
  return (
    <div className="mx-auto text-white">
      <EdgeLinkTable />
    </div>
  );
}
